// The function-invocation LEDGER (exposed as part of app.functions).
//
// `await app.functions.invoke(name, input)` writes the sub-scoped invocation
// record (.s3p/functions/invocations/), invokes the function's own Lambda, and
// polls .s3p/functions/results/ until the handler publishes a TaskResult.
// Not a queue: nothing triggers on these objects — they exist so calls
// are visible (console "in flight" view) and survive a reload.
//
// Trigger kind on the task is always "rpc" — this is the explicit-call
// path. Schedule + s3-event triggers are enqueued by the runtime itself.

import {
  DeleteObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";

import { ulid } from "@s3p/s3db";

import type { InboxTask, TaskResult } from "./functions.ts";
import type { JwtClaims } from "./oidc.ts";
import type { Paths } from "./paths.ts";

export interface CallOpts {
  /** Hard timeout for the entire call (poll loop included). Default 30_000 ms. */
  timeoutMs?: number;
  /** Inter-poll delay. Default 500 ms. */
  pollIntervalMs?: number;
  /** Override which bucket prefix to write the inbox task under. Defaults
   *  to the SDK's configured `bucketPrefix`. Use for cross-project RPC
   *  (a platform-admin invoking a function on a different project's
   *  runtime) — only works if the caller's policy grants write to that
   *  prefix's `inbox/`. */
  bucketPrefix?: string;
  /** Called once per poll attempt while the output hasn't been written
   *  yet. Apps use this to surface progress (status pill, console log,
   *  spinner tick). The SDK is silent by default — no console noise
   *  unless an app opts in. */
  onPoll?: (info: { taskId: string; attempt: number; elapsedMs: number }) => void;
}

/** Structured error thrown by `app.functions.invoke()` and `app.functions.awaitResult()`.
 *  Carries enough context that a `console.error(e)` already reads
 *  cleanly without the app needing to manually prefix the function name
 *  / taskId, and so calling code can branch on `reason` programmatically:
 *
 *    try { await app.functions.invoke("provision-project", input); }
 *    catch (e) {
 *      if (e instanceof CallError && e.reason === "timeout") {
 *        // tell the operator the runtime is offline
 *      } else throw e;
 *    }
 *
 *  When the handler itself threw, the remote stack is appended to this
 *  error's `.stack` so it's visible in console output. */
export class CallError extends Error {
  readonly functionName: string;
  readonly taskId: string;
  /** `handler-error` — the runtime executed the handler, it threw, and
   *  the runtime wrote an error output.
   *  `runtime-error` — the runtime crashed outside the handler (failed
   *  inbox read, failed output write, etc.). Same observable behavior
   *  as handler-error but `error.name` will be `"RuntimeError"`.
   *  `timeout` — the output blob never appeared inside `timeoutMs`. */
  readonly reason: "handler-error" | "runtime-error" | "timeout";

  constructor(opts: {
    functionName: string;
    taskId: string;
    reason: CallError["reason"];
    message: string;
    remoteName?: string;
    remoteStack?: string;
  }) {
    super(`rpc ${opts.functionName}(${opts.taskId}): ${opts.message}`);
    this.name = "CallError";
    this.functionName = opts.functionName;
    this.taskId = opts.taskId;
    this.reason = opts.reason;
    if (opts.remoteStack) {
      this.stack =
        `${this.stack ?? ""}\n\n--- handler stack (from runtime) ---\n${opts.remoteStack}`;
    }
  }
}

export interface InflightTask {
  taskId: string;
  function: string;
  enqueuedAt: string;
  enqueuedBy?: { sub?: string; preferred_username?: string };
  input?: unknown;
  /** Full S3 key of the inbox blob — handy for cross-referencing. */
  inboxKey: string;
}

export interface ListInflightOpts {
  /** Filter to one function name. Default: return all in-flight tasks. */
  function?: string;
  /** Cap the number of objects scanned. Default 200. */
  maxKeys?: number;
  /** "own" (default) lists only YOUR tasks — the sub-scoped prefix every
   *  member can read. "all" lists every user's (the console's admin view);
   *  IAM decides — without read-all it just 403s. */
  scope?: "own" | "all";
}

/** A completed call's result record (`.s3p/functions/results/{sub}/`).
 *  Metadata only — read the payload with `readResult(taskId)`. */
export interface ResultEntry {
  taskId: string;
  /** Full S3 key of the result blob. */
  key: string;
  /** When the runtime wrote the result (S3 LastModified). */
  completedAt?: string;
  size: number;
}

export interface CallsModule {
  /** Enqueue a task and await its result. Throws on handler error or timeout. */
  invoke(name: string, input?: unknown, opts?: CallOpts): Promise<unknown>;
  /** Subscribe to an existing task by its taskId. Polls for its output
   *  blob; resolves with the result on `status: "ok"`, throws on
   *  `status: "error"`, throws on timeout. Same poll semantics as
   *  `call()` — use this to "re-attach" to a task that was enqueued
   *  in a previous session / page load (e.g. /admin/ after reload
   *  finds in-flight tasks via `listInflight()` and awaits each).
   *  Pass `functionName` so thrown `CallError`s carry the human-
   *  readable name (otherwise it's "(unknown)"). */
  awaitResult(
    taskId: string,
    opts?: CallOpts & { functionName?: string },
  ): Promise<unknown>;
  /** Read currently-in-flight tasks from `{bucketPrefix}inbox/`. An entry
   *  exists for any task that has been enqueued but not yet completed
   *  (the runtime deletes the inbox blob after writing the output).
   *  Cross-reference each task's `input` against your local state to
   *  show "in flight" status across page reloads / tabs. */
  listInflight(opts?: ListInflightOpts): Promise<InflightTask[]>;
  /** Delete one of YOUR OWN in-flight records without waiting for a result.
   *  For tasks that will never complete — an enqueue that died mid-deploy, a
   *  runtime that crashed before writing its output — whose records otherwise
   *  sit in the inbox forever (the runtime only deletes on completion). IAM
   *  scopes the delete to your own sub prefix, so you can't dismiss someone
   *  else's task. Dismissing does NOT cancel a running handler: if the task is
   *  actually alive it will still finish and write its output. */
  dismissInflight(taskId: string, opts?: { bucketPrefix?: string }): Promise<void>;
  /** List completed results, newest first (taskIds are ULIDs, so the key
   *  order is the time order). The runtime keeps results until something
   *  cleans them — this is the "recent outcomes" view next to `listInflight`'s
   *  "still running" view. Same `scope` semantics as `listInflight`. */
  listResults(opts?: { maxKeys?: number; scope?: "own" | "all" }): Promise<ResultEntry[]>;
  /** Read one result's raw record (`status` + `result`/`error`), or null if it
   *  hasn't landed / was cleaned. Unlike `awaitResult` this never polls and
   *  never throws on a handler error — it's the inspection read. */
  readResult(taskId: string): Promise<TaskResult | null>;
}

export interface CallsConfig {
  bucket: string;
  bucketPrefix: string;
  /** Where the inbox/outputs live for THIS project's layout. Never build these
   *  keys by hand: the writer (here) and the deleter (the Lambda) must agree, and
   *  they live in different packages that never call each other. */
  paths: Paths;
  getS3Client: () => Promise<S3Client>;
  getClaims: () => JwtClaims | null;
  /** Enqueue by invoking the per-function Lambda async (invoker.enqueue). This
   *  is THE transport — `call()` throws without it. */
  enqueue: (name: string, input: unknown, taskId: string) => Promise<void>;
}

export function createCalls(cfg: CallsConfig): CallsModule {
  /** Poll outputs/{taskId}.json until the runtime writes it. Same
   *  poll semantics regardless of who enqueued the task — used by both
   *  `call()` (after enqueue) and `awaitResult()` (re-attach). On any
   *  failure throws CallError with `functionName` / `taskId` / `reason`. */
  async function pollOutputUntilDone(
    taskId: string,
    functionName: string,
    opts: CallOpts,
  ): Promise<unknown> {
    const prefix = opts.bucketPrefix ?? cfg.bucketPrefix;
    const timeoutMs = opts.timeoutMs ?? 30_000;
    const pollMs = opts.pollIntervalMs ?? 500;
    // SUB-SCOPED: outputs live under the enqueuer's own prefix, and IAM grants
    // read only there. (A flat shared outputs directory once let ANY signed-in
    // user read another's function results — send-email recipients,
    // provisioning configs.)
    const sub = cfg.getClaims()?.sub ?? "anon";
    const outputKey = `${prefix}${cfg.paths.fnResult(sub, taskId)}`;
    const s3 = await cfg.getS3Client();
    const startedAt = Date.now();
    const deadline = startedAt + timeoutMs;
    let attempt = 0;
    // Poll by LIST instead of GET. The output blob doesn't exist until
    // the runtime finishes; a GET would return 404 every tick — which
    // DevTools surfaces as red error entries even though we catch the
    // exception. ListObjectsV2 with the exact key as prefix returns 200
    // + empty Contents while we wait, then 200 + Contents=[outputKey]
    // when it appears. Quieter logs, identical semantics.
    while (Date.now() < deadline) {
      attempt++;
      opts.onPoll?.({
        taskId,
        attempt,
        elapsedMs: Date.now() - startedAt,
      });
      const listed = await s3.send(
        new ListObjectsV2Command({
          Bucket: cfg.bucket,
          Prefix: outputKey,
          MaxKeys: 1,
        }),
      );
      const hit = (listed.Contents ?? []).find((o) => o.Key === outputKey);
      if (hit) {
        const res = await s3.send(
          new GetObjectCommand({ Bucket: cfg.bucket, Key: outputKey }),
        );
        const text = await (
          res.Body as { transformToString: () => Promise<string> }
        ).transformToString();
        const parsed = JSON.parse(text) as TaskResult;
        if (parsed.status === "ok") return parsed.result;
        // The runtime distinguishes handler errors from runtime-level
        // crashes (the catch-all in runtime.ts:sweepInbox sets
        // error.name to "RuntimeError"). We preserve that distinction
        // on the structured error so callers can branch on `reason`.
        const remoteName = parsed.error?.name;
        const reason: CallError["reason"] =
          remoteName === "RuntimeError" ? "runtime-error" : "handler-error";
        throw new CallError({
          functionName,
          taskId,
          reason,
          message: parsed.error?.message ?? "rpc handler returned error",
          remoteName,
          remoteStack: parsed.error?.stack,
        });
      }
      await new Promise((r) => setTimeout(r, pollMs));
    }
    throw new CallError({
      functionName,
      taskId,
      reason: "timeout",
      message: `timed out after ${timeoutMs}ms waiting for ${outputKey}`,
    });
  }

  return {
    async invoke(name, input, opts = {}) {
      if (!/^[a-z][a-z0-9-]*$/.test(name)) {
        throw new Error(`app.functions.invoke: invalid function name '${name}'`);
      }
      const prefix = opts.bucketPrefix ?? cfg.bucketPrefix;
      const taskId = ulid();
      const claims = cfg.getClaims();

      // 1. Record the task under .s3p/functions/invocations/{sub}/{taskId}.json.
      //
      // This is a RECORD, not a transport: nothing triggers on it any more (the
      // bucket has no notification for this prefix). It exists so an in-progress
      // task is VISIBLE — `listInflight()` reads it, which is how the console
      // shows "project X is still provisioning" across a reload or a second
      // operator's browser. The runtime deletes it when it writes the output.
      //
      // Written BEFORE the invoke, deliberately: a handler that finishes fast
      // would otherwise delete a record that hasn't landed yet, leaving a
      // phantom task inflight forever.
      //
      // Best-effort — a project whose IAM predates the inbox grant can still
      // run functions; it just won't show them in the inflight list.
      const task: InboxTask = {
        version: 1,
        taskId,
        function: name,
        enqueuedAt: new Date().toISOString(),
        enqueuedBy: claims
          ? {
              sub: claims.sub,
              preferred_username: (claims as { preferred_username?: string }).preferred_username,
            }
          : undefined,
        trigger: { kind: "rpc" },
        input,
      };
      const inboxKey = `${prefix}${cfg.paths.fnInvocation(claims?.sub ?? "anon", taskId)}`;
      let recorded = false;
      try {
        const s3 = await cfg.getS3Client();
        await s3.send(
          new PutObjectCommand({
            Bucket: cfg.bucket,
            Key: inboxKey,
            Body: JSON.stringify(task, null, 2),
            ContentType: "application/json",
          }),
        );
        recorded = true;
      } catch {
        // non-fatal: the task still runs, it's just not listed as inflight
      }

      // 2. Run it: invoke the per-function Lambda async. THE transport.
      //
      // If the invoke itself fails, the task never ran and no runtime will ever
      // delete the record written above — so take it back out, or the console
      // shows a phantom "in progress" row forever (nothing else cleans the
      // inbox; the UI keys purely off record presence).
      try {
        await cfg.enqueue(name, input, taskId);
      } catch (e) {
        if (recorded) {
          try {
            const s3 = await cfg.getS3Client();
            await s3.send(new DeleteObjectCommand({ Bucket: cfg.bucket, Key: inboxKey }));
          } catch {
            // best-effort — the stale record is dismissible from the console
          }
        }
        throw e;
      }

      return pollOutputUntilDone(taskId, name, opts);
    },

    async awaitResult(taskId, opts = {}) {
      return pollOutputUntilDone(taskId, opts.functionName ?? "(unknown)", opts);
    },

    async listInflight(opts = {}) {
      // Your OWN in-flight tasks by default (v1 listed the whole inbox);
      // scope "all" is the operator view over every sub's partition.
      const sub = opts.scope === "all" ? undefined : cfg.getClaims()?.sub;
      const prefix = `${cfg.bucketPrefix}${cfg.paths.fnInvocationDir(sub)}`;
      const maxKeys = opts.maxKeys ?? 200;
      const s3 = await cfg.getS3Client();
      const listed = await s3.send(
        new ListObjectsV2Command({
          Bucket: cfg.bucket,
          Prefix: prefix,
          MaxKeys: maxKeys,
        }),
      );
      const out: InflightTask[] = [];
      for (const obj of listed.Contents ?? []) {
        if (!obj.Key || !obj.Key.endsWith(".json")) continue;
        try {
          const res = await s3.send(
            new GetObjectCommand({ Bucket: cfg.bucket, Key: obj.Key }),
          );
          const text = await (
            res.Body as { transformToString: () => Promise<string> }
          ).transformToString();
          const task = JSON.parse(text) as InboxTask;
          if (opts.function && task.function !== opts.function) continue;
          out.push({
            taskId: task.taskId,
            function: task.function,
            enqueuedAt: task.enqueuedAt,
            enqueuedBy: task.enqueuedBy,
            input: task.input,
            inboxKey: obj.Key,
          });
        } catch {
          // listed-but-disappeared / unparseable — skip
        }
      }
      return out;
    },

    async dismissInflight(taskId, opts = {}) {
      const sub = cfg.getClaims()?.sub;
      if (!sub) throw new Error("platform.rpc.dismissInflight: not signed in");
      const prefix = opts.bucketPrefix ?? cfg.bucketPrefix;
      const s3 = await cfg.getS3Client();
      await s3.send(
        new DeleteObjectCommand({
          Bucket: cfg.bucket,
          Key: `${prefix}${cfg.paths.fnInvocation(sub, taskId)}`,
        }),
      );
    },

    async listResults(opts = {}) {
      const sub = opts.scope === "all" ? undefined : cfg.getClaims()?.sub;
      const prefix = `${cfg.bucketPrefix}${cfg.paths.fnResultDir(sub)}`;
      const s3 = await cfg.getS3Client();
      const listed = await s3.send(
        new ListObjectsV2Command({
          Bucket: cfg.bucket,
          Prefix: prefix,
          MaxKeys: opts.maxKeys ?? 200,
        }),
      );
      return (listed.Contents ?? [])
        .filter((o) => o.Key?.endsWith(".json"))
        .map((o) => ({
          // Under scope "all" the tail is `{sub}/{taskId}.json` — the taskId
          // is always the last segment.
          taskId: (o.Key ?? "").split("/").pop()?.replace(/\.json$/, "") ?? "",
          key: o.Key ?? "",
          completedAt: o.LastModified?.toISOString(),
          size: o.Size ?? 0,
        }))
        .sort((a, b) => b.taskId.localeCompare(a.taskId));
    },

    async readResult(taskId) {
      const sub = cfg.getClaims()?.sub ?? "anon";
      const s3 = await cfg.getS3Client();
      try {
        const res = await s3.send(
          new GetObjectCommand({
            Bucket: cfg.bucket,
            Key: `${cfg.bucketPrefix}${cfg.paths.fnResult(sub, taskId)}`,
          }),
        );
        const text = await (
          res.Body as { transformToString: () => Promise<string> }
        ).transformToString();
        return JSON.parse(text) as TaskResult;
      } catch {
        return null;
      }
    },
  };
}
